import {
  TableauViz
} from "@tableau/embedding-api";
import classNames from "classnames";
import flatpickr from "flatpickr";
import isMobile from "is-mobile";
import { useEffect, useState } from "react";

import ArrowLeftBack from "../../icons/ArrowBack";
import TableauVizFilterDropdown from "./TableauVizFilterDropdown";
import TableauVizParameterDropdown from "./TableauVizParameterDropdown";

import {
  TABLEAU_FILTERS_ORDER,
  TABLEAU_PARAMS_ORDER
} from "./tableauCustomData";

import "flatpickr/dist/flatpickr.min.css";
import { useTableauFilters } from "./hooks/useTableauFilters";
import "./index.scss";

const TableauFilter = ({
  viz,
  reportAlias,
}: {
  viz: TableauViz;
  reportAlias: string;
}) => {


  const relativeDateOptions = [
    "Today",
    "Yesterday",
    "YTD",
    "Last Week",
    "Last Month",
    "MTD",
    "Last Quarter",
    "Last Year",
  ];

  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const categories = [
    { title: "DATES", category: "date" },
    { title: "HIERARCHY", category: "hierarchy" },
    { title: "ADDITIONAL", category: "additional" },
  ];

  type CategoryType = typeof categories[number]["category"];

  const [selectedFilterCategory, setSelectedFilterCategory] = useState<CategoryType>(categories[0].category);

  const {
    filters,
    parameters,
    filtersLoaded,
    parametersLoaded,
    initFilters,
    initParams,
    applyFilter,
    applyParameter,
    dateRangeSelected,
    selectedDateOption,
    applyRelativeDateFilter,
    setDateRangeSelected,
    setSelectedDates,
  } = useTableauFilters(viz, reportAlias);

  useEffect(() => {
    initFilters();
    initParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKeys]);

  useEffect(() => {
    initializeFlatPicker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterCategory]);

  const initializeFlatPicker = () => {
    if (flatpickr) {
      const input = document.querySelector("#datePicker");

      if (input) {
        flatpickr(input, {
          mode: "range",
          onChange: (dates) => {
            // Ensure two dates (start and end) are selected
            if (dates.length === 2) {
              // Set the selectedDates state with the Date objects directly
              setSelectedDates(dates);

              setDateRangeSelected(true);
            }
          },
        });
      }
    }
  };

  const handleClick = () => {
    const filterWrapper = document.getElementById("filter-wrapper");
    if (!filterWrapper) return;
    filterWrapper.classList.toggle("active");
  };

  const renderFilters = () => {
    if (selectedFilterCategory === "date") {
      return <div className="px-2 w-full">
        <div className="w-full flex justify-center items-center border-b-[0.5px] mb-3 py-3 border-solid border-custom-darkTeal">
          <h4 className="category-header">DATES</h4>
        </div>
        <div className="w-full flex flex-col gap-2 items-start">
          <p className="font-primary font-white font-normal text-sm not-italic leading-[100%]">
            DATE RANGE
          </p>
          <p className="w-full">
            <input
              type="text"
              id="datePicker"
              className={classNames(
                "bg-custom-darkTeal px-4 py-2 text-sm font-normal text-white rounded-md outline-none !h-10",
                "placeholder:text-white placeholder:text-sm placeholder:font-primary placeholder:opacity-60 placeholder:not-italic placeholder:font-normal",
                {
                  "bg-primary": dateRangeSelected,
                }
              )}
              placeholder="Select Date Range"
            />
          </p>
        </div>

        <div className="w-full py-4 bg-transparent grid grid-cols-2 gap-2">
          {relativeDateOptions.map((dateOption, i) => (
            <button
              key={`${dateOption}-${i}`}
              id={`${dateOption}-${i}`}
              className={classNames(
                "bg-custom-darkTeal px-4 py-2 text-sm font-normal text-white rounded-md !h-10",
                {
                  "bg-primary text-white":
                    selectedDateOption === dateOption &&
                    !dateRangeSelected,
                }
              )}
              onClick={() => applyRelativeDateFilter(dateOption)}
            >
              {dateOption}
            </button>
          ))}
        </div>
      </div>
    } else {
      return !filtersLoaded || Object.keys(filters).length === 0 ?
        <div className="flex justify-center items-center m-auto my-4">
          <div className="border-2 border-gray-300 border-t-primary rounded-full w-8 h-8 animate-spin"></div>
        </div>
        : <div className="px-2 w-full">
          <div className="w-full flex justify-center items-center border-b-[0.5px] mb-3 py-3 border-solid border-custom-darkTeal">
            <h4 className="category-header">Hierarchy</h4>
          </div>
          <div className="gap-2 grid grid-cols-2 w-full">
            {Object.keys(filters)
              .filter((key) => filters[key].category === selectedFilterCategory)
              .sort((a, b) => {
                const order = TABLEAU_FILTERS_ORDER[reportAlias] || [];
                return order.indexOf(a) - order.indexOf(b);
              })
              .map((key) => (
                <TableauVizFilterDropdown
                  key={key}
                  filterKey={key}
                  filterData={filters[key]}
                  applyFilter={applyFilter}
                  activeKeys={activeKeys}
                  setActiveKeys={setActiveKeys}
                  selectedFilterCategory={selectedFilterCategory}
                />
              ))}
          </div>
        </div>
    }
  }

  const renderParams = () => {
    return <div className="gap-2 px-2 w-full">
      <div className="flex justify-center items-center mb-3 py-3 border-custom-darkTeal border-t-[0.5px] border-b-[0.5px] border-solid w-full">
        <h4 className="text-primary">Group By</h4>
      </div>

      {!parametersLoaded || Object.keys(parameters).length === 0 ? (
        <div className="flex justify-center items-center m-auto my-4">
          <div className="border-2 border-gray-300 border-t-primary rounded-full w-8 h-8 animate-spin"></div>
        </div>
      ) : (
        <div className="gap-2 grid grid-cols-2 w-full">
          {Object.keys(parameters)
            .sort((a, b) => {
              const order = TABLEAU_PARAMS_ORDER[reportAlias] || [];
              return order.indexOf(a) - order.indexOf(b);
            })
            .map((key) => (
              <TableauVizParameterDropdown
                key={key}
                parameterKey={key}
                parameterData={parameters[key]}
                applyParameter={applyParameter}
              />
            ))}
        </div>
      )}
    </div>
  }

  return (
    <div className="tableau-filter-wrapper">
      <div
        id="filter-wrapper"
        className="top-0 right-0 [border-left:1px_solid_#9999994d] z-[99] fixed flex flex-col justify-around items-center bg-opposite py-[10px] pr-[0] pl-[0] w-full max-w-[480px] h-full overflow-hidden"
      >
        <div className="main-wrapper">
          <div
            className={classNames("flex justify-start items-center w-full", {
              "mt-3 mb-4": !isMobile(),
              "mt-4": isMobile(),
            })}
          >
            <div className="pr-1" onClick={handleClick}>
              <div>
                <ArrowLeftBack className="text-primaryText" />
              </div>
            </div>

            <h4 className="ml-6 font-primary font-semibold text-primaryText text-2xl text-center not-italic leading-[100%]">
              FILTERS
            </h4>
          </div>

          <div className={"flex flex-col gap-4 w-full"} >
            <div className="grid grid-cols-3 gap-4 justify-between items-center w-full h-10">
              {categories.map((c, i) => (
                <button
                  key={i}
                  className={classNames(
                    "font-primary px-2 py-2 text-sm font-normal text-white rounded-md !h-full bg-custom-darkTeal",
                    {
                      "!bg-primary": selectedFilterCategory === c.category,
                    }
                  )}
                  onClick={() => setSelectedFilterCategory(c.category)}
                >
                  {c.title}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center mt-2 w-full overflow-scroll color-white">
            {renderFilters()}

            {renderParams()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableauFilter;
